import { useEffect, useState } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

/** Reflète `behavioral_alert_engine.alertes_du_client` (GET /v1/portfolio/behavioral-alerts). */
export interface AlerteComportementale {
  id: number;
  date_signal: string;
  symbole: string | null;
  nom_societe: string | null;
  signal_type: string;
  titre: string;
  emoji: string;
  severite: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  severite_libelle: string;
  /** Ce qui a été constaté dans les transactions du client, chiffré. */
  constat: string | null;
  /** Pourquoi ce comportement est un risque (texte pédagogique du type d'alerte). */
  explication: string;
  recommandation: string;
}

/**
 * Alertes comportementales détaillées du client. Remplace l'affichage des messages résumés
 * (`useUserMessages('BEHAVIORAL_ALERT')`), qui ne disaient que « 1 comportement à risque
 * détecté » et renvoyaient vers cette même page pour un détail qui n'y figurait pas.
 */
export const useBehavioralAlerts = (actif: boolean) => {
  const { token } = useAuth();
  const [alertes, setAlertes] = useState<AlerteComportementale[]>([]);
  const [loading, setLoading] = useState<boolean>(actif);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!actif || !token) {
      setLoading(false);
      return;
    }
    let annule = false;
    const charger = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/v1/portfolio/behavioral-alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const corps: { data: AlerteComportementale[] } = await res.json();
        if (!annule) setAlertes(corps.data ?? []);
      } catch {
        if (!annule)
          setError("Vos alertes comportementales n'ont pas pu être chargées. Réessayez plus tard.");
      } finally {
        if (!annule) setLoading(false);
      }
    };
    charger();
    return () => {
      annule = true;
    };
  }, [actif, token]);

  return { alertes, loading, error };
};
